import {
    useIsMutating,
    useMutation,
    useMutationState,
    useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";

import {
    conversationDetailQueryKey,
    conversationDetailQueryOptions,
    updateConversation,
} from "@/api";
import type { Page } from "@/api/api.types";
import type {
    IChatConversation,
    IChatConversationDetail,
    IChatConversationListItem,
} from "@/api/chat/chat.types";
import { OWNED_CONVERSATIONS_KEY } from "@/api/chat/conversationListKeys";
import { ownedConversationsQueryOptions } from "@/pages/frontend/chat/adapters/api-thread-list-adapter";
import {
    cancelQueryRefetch,
    refreshQueryAfterWrite,
} from "@/pages/frontend/chat/utils/conversationQueries";
import { invalidateConversationLists } from "@/pages/frontend/chat/utils/invalidateConversationLists";
import { activityTime } from "@/utils/chatConversations";

const PIN_MUTATION_KEY = "conversation-pin";

interface PinChange {
    id: string;
    isPinned: boolean;
}

interface PinSnapshot {
    row?: IChatConversationListItem;
    detail?: IChatConversationDetail;
}

type OwnedPage = Page<IChatConversationListItem>;

const isRow = (id: string) => (conversation: IChatConversation) =>
    conversation.id.toString() === id;

const activityAt = (c: IChatConversationListItem) =>
    Date.parse(activityTime(c));

// Pinned and Recents follow cache position, so a toggled row has to move to
// the slot the server's activity order gives it.
const byActivity = (items: IChatConversationListItem[]) =>
    [...items].sort((a, b) => activityAt(b) - activityAt(a) || b.id - a.id);

function withPin(page: OwnedPage, id: string, isPinned: boolean): OwnedPage {
    const row = page.items.find(isRow(id));
    if (!row || row.is_pinned === isPinned) return page;
    return {
        ...page,
        items: byActivity(
            page.items.map((c) =>
                c === row ? { ...c, is_pinned: isPinned } : c
            )
        ),
    };
}

const applyPinUpdate = <T extends IChatConversation>(
    current: T,
    updated: IChatConversation
): T => ({
    ...current,
    is_pinned: updated.is_pinned,
    updated_at:
        Date.parse(updated.updated_at) > Date.parse(current.updated_at)
            ? updated.updated_at
            : current.updated_at,
});

function withUpdatedRow(
    page: OwnedPage,
    id: string,
    updated: IChatConversation
): OwnedPage {
    const matches = isRow(id);
    return {
        ...page,
        items: byActivity(
            page.items.some(matches)
                ? page.items.map((c) =>
                      matches(c) ? applyPinUpdate(c, updated) : c
                  )
                : [
                      {
                          ...updated,
                          message_count: 0,
                          last_message_at: null,
                          last_message_snippet: null,
                          access_mode: "owner",
                          shared_by_display_name: null,
                      },
                      ...page.items,
                  ]
        ),
    };
}

// A server read that reports the new pin carries a newer updated_at, so only a
// flip no read has replaced is reverted.
const holdsOptimisticPin = (
    current: IChatConversation,
    before: IChatConversation | undefined
) =>
    before !== undefined &&
    current.is_pinned !== before.is_pinned &&
    current.updated_at === before.updated_at;

/** Lay every pin whose PUT is still in flight over the owned page. */
export function useOwnedPageWithPendingPins(
    page: OwnedPage | undefined
): OwnedPage | undefined {
    const pending = useMutationState({
        filters: { mutationKey: [PIN_MUTATION_KEY], status: "pending" },
        select: (mutation) => mutation.state.variables as PinChange,
    });
    return useMemo(
        () =>
            pending.reduce<OwnedPage | undefined>(
                (current, pin) =>
                    current && withPin(current, pin.id, pin.isPinned),
                page
            ),
        [page, pending]
    );
}

export function usePinConversation(itemId: string | undefined) {
    const queryClient = useQueryClient();
    const mutationKey = [PIN_MUTATION_KEY, itemId];
    const isPending = useIsMutating({ mutationKey, exact: true }) > 0;

    const mutation = useMutation({
        mutationKey,
        scope: { id: `${PIN_MUTATION_KEY}:${itemId}` },
        mutationFn: ({ id, isPinned }: PinChange) =>
            updateConversation(id, { is_pinned: isPinned }),
        onMutate: async ({ id, isPinned }): Promise<PinSnapshot> => {
            const detailKey = conversationDetailQueryKey(id);
            await Promise.all([
                cancelQueryRefetch(queryClient, OWNED_CONVERSATIONS_KEY),
                cancelQueryRefetch(queryClient, detailKey),
            ]);
            const snapshot = {
                row: queryClient
                    .getQueryData<OwnedPage>(OWNED_CONVERSATIONS_KEY)
                    ?.items.find(isRow(id)),
                detail: queryClient.getQueryData<IChatConversationDetail>(
                    detailKey
                ),
            };
            queryClient.setQueryData<IChatConversationDetail>(
                detailKey,
                (old) => old && { ...old, is_pinned: isPinned }
            );
            queryClient.setQueryData<OwnedPage>(
                OWNED_CONVERSATIONS_KEY,
                (old) => old && withPin(old, id, isPinned)
            );
            return snapshot;
        },
        onError: (_error, { id, isPinned }, snapshot) => {
            const detailKey = conversationDetailQueryKey(id);
            const page = queryClient.getQueryData<OwnedPage>(
                OWNED_CONVERSATIONS_KEY
            );
            const row = page?.items.find(isRow(id));
            if (page && row && holdsOptimisticPin(row, snapshot?.row)) {
                queryClient.setQueryData(
                    OWNED_CONVERSATIONS_KEY,
                    withPin(page, id, !isPinned)
                );
            }
            const detail =
                queryClient.getQueryData<IChatConversationDetail>(detailKey);
            if (detail && holdsOptimisticPin(detail, snapshot?.detail)) {
                queryClient.setQueryData<IChatConversationDetail>(detailKey, {
                    ...detail,
                    is_pinned: !isPinned,
                });
            }
            void queryClient.invalidateQueries({
                queryKey: OWNED_CONVERSATIONS_KEY,
                exact: true,
            });
            void queryClient.invalidateQueries({
                queryKey: detailKey,
                exact: true,
            });
            toast.error(
                isPinned
                    ? "Couldn't pin conversation"
                    : "Couldn't unpin conversation"
            );
        },
        onSuccess: async (updated, { id }) => {
            void refreshQueryAfterWrite(
                queryClient,
                conversationDetailQueryOptions(id),
                (old) => old && applyPinUpdate(old, updated)
            );
            void refreshQueryAfterWrite(
                queryClient,
                ownedConversationsQueryOptions,
                (old) => old && withUpdatedRow(old, id, updated)
            );
            await invalidateConversationLists(queryClient);
        },
    });

    const setPinned = (isPinned: boolean) => {
        if (
            itemId == null ||
            queryClient.isMutating({ mutationKey, exact: true })
        ) {
            return;
        }
        mutation.mutate({ id: itemId, isPinned });
    };

    return { isPending, setPinned };
}
